import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Row-level status values. A superset of goal status for general usage. */
export type StatusDotStatus =
  | 'on-track'
  | 'ahead'
  | 'warning'
  | 'off-track'
  | 'completed'
  | 'not-started'
  | 'info'
  | 'neutral';

/**
 * Tiny colored dot for row-level status indicators. Pairs with a visible label or
 * an `ariaLabel` — screen readers never see the dot alone. Adapts to dark mode via
 * semantic background tokens.
 *
 * For canonical goal status pills, use `GoalStatusIndicatorComponent`.
 *
 * @example
 * <div class="flex items-center gap-2">
 *   <app-status-dot status="on-track" ariaLabel="On track" />
 *   <span>Math goals</span>
 * </div>
 *
 * @feature shared
 */
@Component({
  selector: 'app-status-dot',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex items-center gap-2' },
  template: `
    <span
      class="inline-block rounded-full"
      [class.w-2]="size() === 'sm'"
      [class.h-2]="size() === 'sm'"
      [class.w-2.5]="size() === 'md'"
      [class.h-2.5]="size() === 'md'"
      [class.w-3]="size() === 'lg'"
      [class.h-3]="size() === 'lg'"
      [class]="bgClass()"
      [attr.role]="label() || ariaLabel() ? 'img' : 'presentation'"
      [attr.aria-label]="ariaLabel() ?? label()"
    ></span>
    @if (label()) {
      <span class="text-sm text-body">{{ label() }}</span>
    }
  `,
})
export class StatusDotComponent {
  /** Status token. @default 'neutral' */
  readonly status = input<StatusDotStatus>('neutral');

  /** Optional visible label. When set, screen readers announce via `aria-label`. */
  readonly label = input<string | undefined>(undefined);

  /** Dot size. @default 'md' */
  readonly size = input<'sm' | 'md' | 'lg'>('md');

  /** Accessible label override (required when no visible `label` is shown). */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Resolved Tailwind background class. */
  readonly bgClass = computed(() => {
    switch (this.status()) {
      case 'on-track':
        return 'bg-primary';
      case 'ahead':
      case 'completed':
        return 'bg-success';
      case 'warning':
        return 'bg-warning';
      case 'off-track':
        return 'bg-danger';
      case 'info':
        return 'bg-blue';
      case 'not-started':
      case 'neutral':
      default:
        return 'bg-medium';
    }
  });
}
